import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Grid,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { useNavigate } from "react-router-dom";
import mgmtSections from "src/services/mgmtSections";

function Result({ sections, fetchSections }) {
  const navigate = useNavigate();

  const [rows, setRows] = useState(sections);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState(null);

  useEffect(() => {
    setRows(sections);
  }, [sections]);

  const handleDeleteClick = (sectionId) => {
    setSelectedSectionId(sectionId);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await mgmtSections.deleteSection(selectedSectionId);

      setDeleteDialogOpen(false);
      setSelectedSectionId(null);

      await fetchSections();
    } catch (error) {
      console.error("Failed to delete section:", error);
    }
  };

  const columns = [
    {
      field: "sectionId",
      headerName: "Section ID",
      width: 120,
    },
    {
      field: "sectionTitle",
      headerName: "Section Title",
      width: 200,
    },
    {
      field: "sectionText",
      headerName: "Section Text",
      width: 400,
    },
    {
      field: "products",
      headerName: "Products",
      width: 300,
      valueGetter: (params) =>
        Array.isArray(params.row.products)
          ? params.row.products
              .map((product) => product.productSKU)
              .join(", ")
          : "",
    },
    {
      field: "action",
      headerName: "Action",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Delete">
          <IconButton
            color="error"
            onClick={() =>
              handleDeleteClick(params.row.sectionId)
            }
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Grid
      container
      sx={{
        width: "90%",
        margin: "15px auto",
        display: "block",
      }}
    >
      {/* Add Section Button */}
      <Grid
        container
        sx={{
          width: "100%",
          mb: 1,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() =>
            navigate(
              "/app/management/sections/create"
            )
          }
        >
          ADD SECTION
        </Button>
      </Grid>

      {/* Sections Table */}
      <Grid
        sx={{
          height: 500,
          width: "100%",
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.sectionId}
          pageSize={10}
          rowsPerPageOptions={[10]}
        />
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedSectionId(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this section?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedSectionId(null);
            }}
          >
            CANCEL
          </Button>

          <Button
            onClick={handleDelete}
            variant="contained"
          >
            CONFIRM
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

Result.propTypes = {
  sections: PropTypes.array,
  fetchSections: PropTypes.func,
};

Result.defaultProps = {
  sections: [],
  fetchSections: () => {},
};

export default Result;